import { useGetCostStatsQuery } from '../../store/api/agentsApi'
import { Card } from '../../components/ui'
import { COLOR_WARNING } from '../../lib/config'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

export function AdminCosts() {
  const { data, isFetching, refetch } = useGetCostStatsQuery()
  const fmt    = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
  const fmtCost = (n: number) => n === 0 ? '$0.00' : n < 0.01 ? `${n.toFixed(4)}` : `${n.toFixed(2)}`
  const fmtMs  = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
  const d = data
  const spark = d?.dailyCosts ?? []
  const maxCost = Math.max(...spark.map(r => r.cost), 0.0001)
  const sparkPath = spark.length > 1
    ? spark.map((r, i) => { const x = (i / (spark.length - 1)) * 200; const y = 40 - (r.cost / maxCost) * 36; return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}` }).join(' ')
    : ''

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>💸 AI Cost Dashboard — Last 30 days</div>
        <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
      </div>

      <div className={s.costsGrid}>
        {[
          { icon: '🔢', label: 'Total Requests', value: fmt(d?.totals30d.requests ?? 0) },
          { icon: '💰', label: 'Total Cost', value: fmtCost(d?.totals30d.costUsd ?? 0) },
          { icon: '📝', label: 'Tokens In', value: fmt(d?.totals30d.tokensIn ?? 0) },
          { icon: '💬', label: 'Tokens Out', value: fmt(d?.totals30d.tokensOut ?? 0) },
          { icon: '⚡', label: 'Avg Latency', value: fmtMs(d?.totals30d.avgDurationMs ?? 0) },
        ].map(stat => (
          <Card key={stat.label} padding="1rem" style={{ textAlign: 'center' }}>
            <div className={s.costStatIcon}>{stat.icon}</div>
            <div className={s.costStatValue}>{d ? stat.value : '…'}</div>
            <div className={s.costStatLabel}>{stat.label}</div>
          </Card>
        ))}
      </div>

      {sparkPath && (
        <div className={s.sparklineCard}>
          <div className={s.sparklineTitle}>Daily cost (last 30 days)</div>
          <svg viewBox="0 0 200 40" style={{ width: '100%', height: '60px' }}>
            <defs>
              <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${sparkPath} L200,40 L0,40 Z`} fill="url(#costGrad)" />
            <path d={sparkPath} fill="none" stroke="var(--accent-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className={s.sparklineDates}>
            <span>{spark[0]?.date?.slice(5) ?? ''}</span>
            <span>{spark[spark.length - 1]?.date?.slice(5) ?? ''}</span>
          </div>
        </div>
      )}

      <div className={s.listCard}>
        <div className={s.costTableHeader}>Cost by Agent</div>
        {!d && <div className={s.listCardLoading}>Loading…</div>}
        {d && d.byAgent.length === 0 && <div className={s.listCardEmpty}>No AI requests recorded yet.</div>}
        {d?.byAgent.map((a) => (
          <div key={a.agentId} className={s.costAgentRow}>
            <div className={s.costAgentInfo}>
              <div className={s.costAgentName}>{a.agentName}</div>
              <div className={s.costAgentSub}>{fmt(a.requests)} requests · avg {fmtMs(a.avgDurationMs)}</div>
            </div>
            <div className={s.costAgentAmount}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: a.costUsd > 1 ? COLOR_WARNING : 'var(--text-primary)' }}>{fmtCost(a.costUsd)}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{fmt(a.tokensIn + a.tokensOut)} tokens</div>
            </div>
            <div className={s.costBarBg}>
              <div className={s.costBarFill} style={{ width: `${Math.min(100, (a.costUsd / (d.byAgent[0]?.costUsd || 1)) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
